import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RecordNav } from "../components/RecordNav";

const codeKey = (c) => c.code || c.name;
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import JsBarcode from "jsbarcode";
import {
  ArrowLeft, FloppyDisk, Printer, Plus, Trash, CaretDown, CaretRight,
  ListChecks, Note as NoteIcon, Barcode, MapPin, ClockCounterClockwise, Coins,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { useAutoSave, AutoSaveIndicator } from "../lib/useAutoSave";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { PROJECT_STATUSES, ProjectStatusChip } from "./Projects";
import { ProjectLedgerTable, useProjectLedger } from "../components/ProjectLedger";
import { usePerms } from "../lib/perms";
import { ViewOnlyBadge } from "../components/Permission";
import { ItemPicker, itemLineDescription } from "../components/LineItemText";

const TASK_TYPES = ["Heading", "Posting", "Total"];

function Section({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const key = title.toLowerCase().replace(/[^a-z]+/g, "-");
  return (
    <div className="border border-border bg-card mb-4" data-testid={`prjsection-${key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-secondary/50 transition-colors" data-testid={`prjsection-toggle-${key}`}>
        {open ? <CaretDown size={16} className="text-primary" /> : <CaretRight size={16} className="text-muted-foreground" />}
        <span className="font-bold text-sm tracking-tight">{title}</span>
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}
function F({ label, children }) {
  return (<div><Label className="overline-label">{label}</Label><div className="mt-1">{children}</div></div>);
}

function BarcodeSVG({ value }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      try { JsBarcode(ref.current, value, { format: "CODE128", width: 2, height: 70, fontSize: 14, margin: 8 }); } catch { /* ignore */ }
    }
  }, [value]);
  return <svg ref={ref} data-testid="project-barcode" />;
}

export default function ProjectCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: project, isLoading } = useQuery({ queryKey: ["project", id], queryFn: async () => (await api.get(`/projects/${id}`)).data });
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: salespeople = [] } = useQuery({ queryKey: ["salespeople"], queryFn: async () => (await api.get("/salespeople")).data });
  const { data: company = {} } = useQuery({ queryKey: ["company-profile"], queryFn: async () => (await api.get("/company-profile")).data });
  const { data: scans = [] } = useQuery({ queryKey: ["project-scans", id], queryFn: async () => (await api.get(`/projects/${id}/scans`)).data, enabled: !!id });
  const { data: items = [] } = useQuery({ queryKey: ["items"], queryFn: async () => (await api.get("/items")).data });
  const { data: ledger } = useProjectLedger(id);
  const lt = ledger?.totals || {};

  const [form, setForm] = useState(null);
  const [tasks, setTasks] = useState([]);
  useEffect(() => {
    if (project) {
      setForm({ ...project });
      setTasks((project.tasks || []).map((t) => ({ ...t })));
    }
  }, [project]);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setTask = (idx, k, v) => setTasks((ts) => ts.map((t, i) => i === idx ? { ...t, [k]: v } : t));
  const addTask = () => setTasks((ts) => [...ts, { task_no: "", description: "", task_type: "Posting", item_id: "", quantity: 0, start_date: "", end_date: "", budget_cost: 0, actual_cost: 0, billable_price: 0, invoiced_price: 0 }]);
  const removeTask = (idx) => setTasks((ts) => ts.filter((_, i) => i !== idx));

  const rollup = useMemo(() => {
    const posting = tasks.filter((t) => t.task_type === "Posting");
    const sum = (k) => posting.reduce((s, t) => s + Number(t[k] || 0), 0);
    return { budget: sum("budget_cost"), actual: sum("actual_cost"), billable: sum("billable_price"), invoiced: sum("invoiced_price") };
  }, [tasks]);

  const taskLedger = useMemo(() => {
    const byItem = { ...(ledger?.cost_by_item || {}) };
    return tasks.map((t) => {
      if (t.task_type !== "Posting" || !t.item_id || byItem[t.item_id] == null) return 0;
      const v = byItem[t.item_id];
      delete byItem[t.item_id];
      return v;
    });
  }, [tasks, ledger]);
  const ledgerTotal = Number(lt.total_cost || 0);
  const unallocated = ledgerTotal - taskLedger.reduce((s, v) => s + v, 0);
  const over = rollup.budget > 0 && ledgerTotal > rollup.budget;

  const buildPayload = () => ({
    name: form.name, description: form.description, customer_id: form.customer_id, manager: form.manager,
    person_responsible: form.person_responsible, external_doc_no: form.external_doc_no, your_reference: form.your_reference,
    status: form.status, start_date: form.start_date, end_date: form.end_date, budget: Number(form.budget),
    tasks: tasks.filter((t) => t.description || t.task_no).map((t) => ({
      task_no: t.task_no, description: t.description, task_type: t.task_type, item_id: t.item_id || "", quantity: Number(t.quantity || 0), start_date: t.start_date, end_date: t.end_date,
      budget_cost: Number(t.budget_cost), actual_cost: Number(t.actual_cost), billable_price: Number(t.billable_price), invoiced_price: Number(t.invoiced_price),
    })),
  });
  const save = useMutation({
    mutationFn: async () => (await api.put(`/projects/${id}`, buildPayload())).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); qc.invalidateQueries({ queryKey: ["project", id] }); toast.success("Project saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const autoSaveFn = async () => {
    const res = (await api.put(`/projects/${id}`, buildPayload())).data;
    qc.invalidateQueries({ queryKey: ["projects"] });
    return res;
  };
  const { can } = usePerms();
  const viewOnly = !can("projects.edit");
  const { status: autoStatus, flush: autoSaveFlush } = useAutoSave({ active: !!form && !viewOnly, canSave: !!form?.name, createMode: false, snapshot: form ? buildPayload() : {}, save: autoSaveFn });

  const [note, setNote] = useState("");
  const addNote = useMutation({
    mutationFn: async () => (await api.post(`/projects/${id}/notes`, { text: note })).data,
    onSuccess: () => { setNote(""); qc.invalidateQueries({ queryKey: ["project", id] }); toast.success("Note added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const printBarcode = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    const cAddr = [company.address, company.address2, company.city, company.state, company.postcode, company.country].filter(Boolean).join(", ");
    const cContact = [company.phone, company.email, company.website].filter(Boolean).join(" · ");
    const cTax = [company.tax_number ? `Tax No. ${company.tax_number}` : "", company.registration_number ? `Reg. ${company.registration_number}` : ""].filter(Boolean).join(" · ");
    const cName = company.name || "Neuclius";
    const logo = company.logo ? `<img src="${company.logo}" style="max-height:60px;max-width:180px;object-fit:contain" />` : "";
    const esc = (s) => (s || "").replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));
    w.document.write(`<html><head><title>${esc(project.code)}</title></head>
      <body style="font-family:Arial;margin:0;padding:32px;color:#0A0A0A">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #F5720D;padding-bottom:16px">
          <div>${logo}<div style="font-size:20px;font-weight:bold;margin-top:6px">${esc(cName)}</div>
          <div style="font-size:11px;color:#52525B">${esc(cAddr)}</div>
          <div style="font-size:11px;color:#52525B">${esc(cContact)}</div></div>
          <div style="text-align:right"><div style="font-size:22px;font-weight:bold;color:#F5720D">PROJECT</div><div style="font-size:13px">${esc(project.code)}</div></div>
        </div>
        <div style="text-align:center;padding:48px 0">
          <h2 style="margin:0 0 4px">${esc(project.name)}</h2>
          <p style="margin:0 0 24px;color:#52525B">Status: ${esc(project.status)}</p>
          <svg id="bc"></svg>
        </div>
        <div style="position:fixed;bottom:24px;left:32px;right:32px;border-top:1px solid #E5E7EB;padding-top:8px;text-align:center;font-size:10px;color:#52525B">
          ${esc([cName, cAddr, cContact, cTax].filter(Boolean).join("  ·  "))}
        </div>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.12.1/dist/JsBarcode.all.min.js"></script>
        <script>JsBarcode("#bc","${esc(project.code)}",{format:"CODE128",width:2,height:90,fontSize:16});setTimeout(function(){window.print();},500);<\/script>
      </body></html>`);
    w.document.close();
  };

  if (isLoading || !form) return <div className="flex items-center justify-center py-24"><div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-up pb-10" data-testid="project-card" onBlur={autoSaveFlush}>
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => navigate("/projects")} className="p-1.5 -ml-1.5 text-muted-foreground hover:text-primary" data-testid="project-card-back"><ArrowLeft size={18} weight="bold" /></button>
      </div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="project-card-title">{project.code} · {project.name}</h1>
        <ProjectStatusChip status={form.status} />
        <ViewOnlyBadge show={viewOnly} />
        <div className="ml-auto"><RecordNav source="projects" currentId={id} sortKey={codeKey} basePath="/projects" testId="project-nav" /></div>
      </div>

      <div className="flex items-center gap-1 flex-wrap border border-border bg-card px-2 py-1.5 mb-5 rounded-sm">
        {!viewOnly && <button onClick={() => save.mutate()} disabled={save.isPending} data-testid="project-ribbon-save" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"><FloppyDisk size={15} weight="bold" /> Save</button>}
        {!viewOnly && <AutoSaveIndicator status={autoStatus} testid="project-autosave" />}
        <button onClick={printBarcode} data-testid="project-print-barcode" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-foreground hover:bg-secondary"><Printer size={15} weight="bold" /> Print Barcode</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <fieldset disabled={viewOnly} className="lg:col-span-2 min-w-0" data-testid="project-form-fields">
          <Section title="General">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="No."><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm font-mono">{project.code}</div></F>
              <F label="Status">
                <Select value={form.status} onValueChange={(v) => setF("status", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="project-card-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Description"><Input data-testid="project-name" value={form.name} onChange={(e) => setF("name", e.target.value)} className="rounded-sm" /></F>
              <F label="Search Description / Notes"><Input value={form.description || ""} onChange={(e) => setF("description", e.target.value)} className="rounded-sm" /></F>
              <F label="Customer No.">
                <Select value={form.customer_id || "none"} onValueChange={(v) => setF("customer_id", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="project-card-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Customer Name"><div className="h-9 flex items-center px-3 border border-border bg-secondary/40 text-sm rounded-sm text-muted-foreground">{customers.find((c) => c.id === form.customer_id)?.name || "—"}</div></F>
              <F label="Project Manager">
                <Select value={form.manager || "none"} onValueChange={(v) => setF("manager", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="project-card-manager"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{salespeople.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Person Responsible">
                <Select value={form.person_responsible || "none"} onValueChange={(v) => setF("person_responsible", v === "none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="project-card-responsible"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{salespeople.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="External Document No."><Input data-testid="project-ext-doc" value={form.external_doc_no || ""} onChange={(e) => setF("external_doc_no", e.target.value)} className="rounded-sm" placeholder="Reference" /></F>
              <F label="Your Reference"><Input value={form.your_reference || ""} onChange={(e) => setF("your_reference", e.target.value)} className="rounded-sm" /></F>
              <F label="Start Date"><Input type="date" value={form.start_date || ""} onChange={(e) => setF("start_date", e.target.value)} className="rounded-sm" /></F>
              <F label="End Date"><Input type="date" value={form.end_date || ""} onChange={(e) => setF("end_date", e.target.value)} className="rounded-sm" /></F>
              <F label="Budget"><Input type="number" value={form.budget} onChange={(e) => setF("budget", e.target.value)} className="rounded-sm num" /></F>
            </div>
          </Section>

          <Section title="Tasks">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[1250px]" data-testid="project-tasks-table">
                <thead>
                  <tr className="text-left border-b border-border">
                    {["Task No.", "Description", "Type", "Item", "Qty", "Start", "End", "Budget Cost", "Ledger Cost", "Actual Cost", "Billable Price", "Invoiced Price", ""].map((h, i) => (
                      <th key={i} className={`py-2 px-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold ${i === 4 || (i >= 7 && i <= 11) ? "text-right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tasks.length === 0 && <tr><td colSpan={13} className="py-6 text-center text-muted-foreground text-sm">No task lines. Add project tasks to structure the job.</td></tr>}
                  {tasks.map((t, idx) => {
                    const heading = t.task_type !== "Posting";
                    return (
                      <tr key={idx} className={`border-b border-border/60 ${heading ? "bg-secondary/40 font-semibold" : ""}`} data-testid="project-task-row">
                        <td className="py-1 px-1.5 w-20 min-w-[72px]"><Input value={t.task_no} onChange={(e) => setTask(idx, "task_no", e.target.value)} className="rounded-sm h-8 text-xs font-mono" data-testid="task-no" /></td>
                        <td className="py-1 px-1.5 min-w-[200px]"><Input value={t.description} onChange={(e) => setTask(idx, "description", e.target.value)} className="rounded-sm h-8 text-sm" data-testid="task-desc" /></td>
                        <td className="py-1 px-1.5 w-28">
                          <Select value={t.task_type} onValueChange={(v) => setTask(idx, "task_type", v)}>
                            <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="task-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{TASK_TYPES.map((tt) => <SelectItem key={tt} value={tt}>{tt}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="py-1 px-1.5 w-40">
                          {heading ? <span className="block text-muted-foreground text-xs">—</span> : (
                            <ItemPicker value={t.item_id || ""} items={items} onChange={(v) => setTasks((ts) => ts.map((x, i) => (i === idx ? { ...x, item_id: v, description: (x.description || "").trim() ? x.description : itemLineDescription(items.find((it) => it.id === v)) } : x)))} allowNone testid="task-item" />
                          )}
                        </td>
                        <td className="py-1 px-1.5 w-20">
                          {heading ? <span className="block text-right text-muted-foreground">—</span> : <Input type="number" value={t.quantity} onChange={(e) => setTask(idx, "quantity", e.target.value)} className="rounded-sm h-8 num text-right text-xs" data-testid="task-quantity" />}
                        </td>
                        <td className="py-1 px-1.5 w-32"><Input type="date" value={t.start_date || ""} onChange={(e) => setTask(idx, "start_date", e.target.value)} className="rounded-sm h-8 text-xs" /></td>
                        <td className="py-1 px-1.5 w-32"><Input type="date" value={t.end_date || ""} onChange={(e) => setTask(idx, "end_date", e.target.value)} className="rounded-sm h-8 text-xs" /></td>
                        {["budget_cost", "ledger", "actual_cost", "billable_price", "invoiced_price"].map((k) => (
                          <td key={k} className="py-1 px-1.5 w-28 min-w-[100px]">
                            {heading ? <span className="block text-right text-muted-foreground">—</span> : k === "ledger" ? (
                              <span className={`block text-right tabular text-xs font-semibold ${Number(t.budget_cost) > 0 && taskLedger[idx] > Number(t.budget_cost) ? "text-destructive" : ""}`} data-testid="task-ledger-cost">{money(taskLedger[idx])}</span>
                            ) : <Input type="number" value={t[k]} onChange={(e) => setTask(idx, k, e.target.value)} className="rounded-sm h-8 num text-right text-xs" data-testid={`task-${k}`} />}
                          </td>
                        ))}
                        <td className="py-1 px-1 w-8">{!viewOnly && <button onClick={() => removeTask(idx)} className="p-1 text-muted-foreground hover:text-destructive" data-testid="task-delete"><Trash size={14} /></button>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!viewOnly && <button onClick={addTask} data-testid="project-add-task" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><Plus size={14} weight="bold" /> New Line</button>}
          </Section>

        </fieldset>

        <div className="lg:col-span-1 space-y-4">
          <div className="border border-border bg-card sticky top-4" data-testid="project-info-pane">
            <Tabs defaultValue="details">
              <TabsList className="w-full rounded-none border-b border-border bg-secondary/40 h-auto p-0">
                <TabsTrigger value="details" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="prj-tab-details"><ListChecks size={14} className="mr-1" /> Details</TabsTrigger>
                <TabsTrigger value="ledger" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="prj-tab-ledger"><Coins size={14} className="mr-1" /> Ledger</TabsTrigger>
                <TabsTrigger value="notes" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="prj-tab-notes"><NoteIcon size={14} className="mr-1" /> Notes</TabsTrigger>
                <TabsTrigger value="barcode" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="prj-tab-barcode"><Barcode size={14} className="mr-1" /> Barcode</TabsTrigger>
                <TabsTrigger value="scans" className="flex-1 rounded-none py-2.5 text-xs data-[state=active]:bg-card" data-testid="prj-tab-scans"><ClockCounterClockwise size={14} className="mr-1" /> Scans</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="p-4 mt-0" data-testid="project-details-rollup">
                <p className="overline-label mb-2">Project Details</p>
                <div className="space-y-3 text-sm">
                  <RollupCard label="Budget Cost" value={rollup.budget} />
                  <RollupCard label="Ledger Cost (posted)" value={ledgerTotal} accent={over} testid="rollup-ledger-cost" />
                  <RollupCard label={over ? "Over Budget" : "Remaining Budget"} value={Math.abs(rollup.budget - ledgerTotal)} accent={over} testid="rollup-budget-variance" />
                  {Math.abs(unallocated) > 0.004 && <RollupCard label="Not matched to a task" value={unallocated} testid="rollup-unallocated" />}
                  <RollupCard label="Actual Cost" value={rollup.actual} accent={rollup.actual > rollup.budget && rollup.budget > 0} />
                  <RollupCard label="Billable Price" value={rollup.billable} />
                  <RollupCard label="Invoiced Price" value={rollup.invoiced} />
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <span className="text-muted-foreground text-xs uppercase tracking-wide">Task Lines</span>
                    <span className="tabular font-bold">{tasks.length}</span>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="ledger" className="p-4 mt-0" data-testid="project-ledger-summary">
                <p className="overline-label mb-2">Posted Cost</p>
                <div className="space-y-3 text-sm">
                  <RollupCard label="Purchase Orders" value={lt.po_cost || 0} />
                  <RollupCard label="Stock Transfers" value={lt.str_cost || 0} />
                  <RollupCard label="of which Items" value={lt.item_cost || 0} />
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <span className="text-muted-foreground text-xs uppercase tracking-wide">Total Cost</span>
                    <span className="tabular font-bold" data-testid="project-ledger-summary-total">{money(lt.total_cost || 0)}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{(ledger?.entries || []).length} ledger entr{(ledger?.entries || []).length === 1 ? "y" : "ies"}</p>
                </div>
              </TabsContent>

              <TabsContent value="notes" className="p-4 mt-0">
                <div className="space-y-2 max-h-56 overflow-y-auto mb-3" data-testid="project-notes-list">
                  {(project.notes || []).length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
                  {(project.notes || []).map((n) => <div key={n.id} className="border border-border rounded-sm p-2.5 text-sm"><p className="whitespace-pre-wrap">{n.text}</p><p className="text-[11px] text-muted-foreground mt-1">{n.created_by} · {(n.created_at || "").slice(0, 10)}</p></div>)}
                </div>
                {!viewOnly && <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note…" data-testid="project-note-input" className="w-full rounded-sm border border-border p-2 text-sm outline-none focus:border-primary resize-none" />}
                {!viewOnly && <Button onClick={() => addNote.mutate()} disabled={!note.trim() || addNote.isPending} data-testid="project-add-note" className="mt-2 rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs w-full"><Plus size={14} className="mr-1" /> Add Note</Button>}
              </TabsContent>

              <TabsContent value="barcode" className="p-4 mt-0 text-center" data-testid="project-barcode-pane">
                <div className="flex justify-center bg-white p-3 rounded-sm border border-border overflow-hidden"><BarcodeSVG value={project.code} /></div>
                <p className="text-xs text-muted-foreground mt-3">Scan this Code128 barcode to look up <span className="font-mono">{project.code}</span> and track its status.</p>
              </TabsContent>

              <TabsContent value="scans" className="p-4 mt-0" data-testid="project-scans-pane">
                <p className="overline-label mb-2">Scan History</p>
                {scans.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No scans recorded yet. Scan this project's barcode to check it in.</p>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="project-scans-list">
                    {scans.map((s) => (
                      <div key={s.id} className="border border-border rounded-sm p-2.5 text-sm" data-testid="project-scan-entry">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{s.user_name}</span>
                          <span className="text-[11px] text-muted-foreground font-mono">{new Date(s.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        </div>
                        <div className="text-xs text-muted-foreground capitalize">{s.role}</div>
                        {s.lat != null && s.lng != null ? (
                          <a href={`https://www.google.com/maps?q=${s.lat},${s.lng}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1" data-testid="project-scan-map-link">
                            <MapPin size={12} weight="fill" /> {s.lat.toFixed(5)}, {s.lng.toFixed(5)}
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-1"><MapPin size={12} /> Location not shared</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <div className="lg:col-span-3">
          <Section title="Project Ledger Entries">
            <ProjectLedgerTable projectId={id} projectCode={project.code} />
          </Section>
        </div>
      </div>
    </div>
  );
}

function RollupCard({ label, value, accent, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-muted-foreground text-xs uppercase tracking-wide">{label}</span>
      <span className={`tabular font-bold ${accent ? "text-destructive" : ""}`}>{money(value)}</span>
    </div>
  );
}
